import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import axios from '../../api';
import useLatestOrgId from './useLatestOrgId';

const SEV_STYLE = {
  CRITICAL: { backgroundColor: '#fee2e2', color: '#991b1b' },
  HIGH:     { backgroundColor: '#ffedd5', color: '#9a3412' },
  MEDIUM:   { backgroundColor: '#fef3c7', color: '#92400e' },
  LOW:      { backgroundColor: '#d1fae5', color: '#065f46' },
  INFO:     { backgroundColor: '#dbeafe', color: '#1e40af' },
};

const RUN_STATUS = {
  PENDING:   { backgroundColor: '#fef3c7', color: '#92400e', icon: 'fa-clock' },
  RUNNING:   { backgroundColor: '#dbeafe', color: '#1e40af', icon: 'fa-spinner fa-spin' },
  COMPLETED: { backgroundColor: '#d1fae5', color: '#065f46', icon: 'fa-check-circle' },
  FAILED:    { backgroundColor: '#fee2e2', color: '#991b1b', icon: 'fa-times-circle' },
};

const MITRE = {
  T1078: { name: i18n.t('BasTab.mitreT1078Name'), tactic: i18n.t('BasTab.mitreT1078Tactic') },
  T1087: { name: i18n.t('BasTab.mitreT1087Name'), tactic: i18n.t('BasTab.mitreT1087Tactic') },
  T1484: { name: i18n.t('BasTab.mitreT1484Name'), tactic: i18n.t('BasTab.mitreT1484Tactic') },
  T1530: { name: i18n.t('BasTab.mitreT1530Name'), tactic: i18n.t('BasTab.mitreT1530Tactic') },
  T1552: { name: i18n.t('BasTab.mitreT1552Name'), tactic: i18n.t('BasTab.mitreT1552Tactic') },
  T1562: { name: i18n.t('BasTab.mitreT1562Name'), tactic: i18n.t('BasTab.mitreT1562Tactic') },
  T1580: { name: i18n.t('BasTab.mitreT1580Name'), tactic: i18n.t('BasTab.mitreT1580Tactic') },
};

function duration(start, end) {
  if (!start) return '—';
  const secs = Math.round((new Date(end || Date.now()) - new Date(start)) / 1000);
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function ts(v) {
  return v ? new Date(v).toLocaleString() : '—';
}

export default function BasTab({ orgId }) {
  const { t } = useTranslation();
  const latestOrgIdRef = useLatestOrgId(orgId);
  const [connectors, setConnectors] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState('');
  const [runs, setRuns] = useState([]);
  const [runsLoaded, setRunsLoaded] = useState(false);
  const [totalRuns, setTotalRuns] = useState(0);
  const [runsPage, setRunsPage] = useState(0);
  const [selectedRun, setSelectedRun] = useState(null);
  const [findings, setFindings] = useState([]);
  const [findingsLoaded, setFindingsLoaded] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState(null);
  const [runsError, setRunsError] = useState(null);
  const [expandedFinding, setExpandedFinding] = useState(null);
  const [copied, setCopied] = useState(null);

  const loadConnectors = useCallback(() => {
    const params = orgId ? { orgId } : {};
    axios.get('/iasm/connectors', { params }).then(r => {
      if (latestOrgIdRef.current !== orgId) return; // org switched while this request was in flight
      const basConns = r.data.filter(c => c.connectorType === 'AWS' || c.connectorType === 'SSH' || c.connectorType === 'AZURE_AD');
      setConnectors(basConns);
      setSelectedConnector(prev => prev || (basConns.length > 0 ? String(basConns[0].id) : ''));
    }).catch(() => {});
  }, [orgId, latestOrgIdRef]);

  const loadRuns = useCallback(() => {
    const params = { page: runsPage, size: 20 };
    if (orgId) params.orgId = orgId;
    axios.get('/iasm/bas/runs', { params })
      .then(r => {
        if (latestOrgIdRef.current !== orgId) return; // org switched while this request was in flight
        setRuns(r.data?.content ?? []);
        setTotalRuns(r.data?.totalElements ?? 0);
        setRunsError(null);
      })
      .catch(e => {
        if (latestOrgIdRef.current !== orgId) return;
        console.error('loadRuns failed:', e?.response?.status, e?.response?.data || e?.message);
        setRunsError(e?.response?.data?.message || e?.response?.data?.error || `HTTP ${e?.response?.status || 'error'}: failed to load runs`);
      })
      .finally(() => { if (latestOrgIdRef.current === orgId) setRunsLoaded(true); });
  }, [runsPage, orgId, latestOrgIdRef]);

  const loadFindings = useCallback((runId) => {
    const params = {};
    if (runId) params.runId = runId;
    if (orgId) params.orgId = orgId;
    setFindingsLoaded(false);
    axios.get('/iasm/bas/findings', { params })
      .then(r => {
        if (latestOrgIdRef.current !== orgId) return; // org switched while this request was in flight
        setFindings(r.data.content);
      })
      .catch(() => {})
      .finally(() => { if (latestOrgIdRef.current === orgId) setFindingsLoaded(true); });
  }, [orgId, latestOrgIdRef]);

  useEffect(() => { setSelectedConnector(''); }, [orgId]);
  useEffect(() => { loadConnectors(); }, [loadConnectors]);
  // Reset only on a genuine new load (org/page change), not on every 8s poll tick below —
  // otherwise the run-history table would flash a spinner every 8 seconds even when nothing changed.
  useEffect(() => { setRunsLoaded(false); }, [orgId, runsPage]);
  useEffect(() => {
    loadRuns();
    const t = setInterval(loadRuns, 8000);
    return () => clearInterval(t);
  }, [loadRuns]);
  useEffect(() => { loadFindings(selectedRun); }, [loadFindings, selectedRun]);

  const selectedConnectorObj = connectors.find(c => String(c.id) === String(selectedConnector));
  const connType = selectedConnectorObj?.connectorType;
  const isSSH = connType === 'SSH';
  const isAzure = connType === 'AZURE_AD';

  const triggerRun = () => {
    if (!selectedConnector) return;
    setTriggering(true);
    setTriggerError(null);
    const endpoint = isSSH ? '/iasm/bas/ssh-runs' : isAzure ? '/iasm/bas/azure-runs' : '/iasm/bas/runs';
    const params = { connectorId: selectedConnector };
    if (orgId) params.orgId = orgId;
    axios.post(endpoint, null, { params })
      .then(() => { loadRuns(); })
      .catch(e => {
        const status = e.response?.status;
        const msg = e.response?.data?.message || e.response?.data?.error || t('BasTab.errFailedToStartRun');
        setTriggerError(status === 402
          ? msg + t('BasTab.errInsufficientCreditsSuffix')
          : msg);
      })
      .finally(() => setTriggering(false));
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div>
      {/* Header + trigger */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 600, color: '#111827' }}>
            {t('BasTab.heading')}
          </h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
            {t('BasTab.headingDescription')}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <select
            value={selectedConnector}
            onChange={e => setSelectedConnector(e.target.value)}
            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
          >
            {connectors.length === 0 && <option value="">{t('BasTab.noAwsConnectors')}</option>}
            {connectors.map(c => (
              <option key={c.id} value={String(c.id)}>{c.name || c.displayName}</option>
            ))}
          </select>
          <button
            onClick={triggerRun}
            disabled={triggering || !selectedConnector}
            style={{
              padding: '8px 18px', background: triggering ? '#9ca3af' : '#dc2626',
              color: '#fff', border: 'none', borderRadius: '6px', cursor: triggering ? 'default' : 'pointer',
              fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <i className={`fas ${triggering ? 'fa-spinner fa-spin' : isSSH ? 'fa-terminal' : isAzure ? 'fa-microsoft' : 'fa-shield-alt'}`} />
            {triggering ? t('BasTab.btnStarting')
              : isSSH ? t('BasTab.btnRunSshProbe')
              : isAzure ? t('BasTab.btnRunAzureBas', 'Run Azure BAS')
              : t('BasTab.btnRunAwsBas')}
            {!triggering && (
              <span style={{ fontSize: '10px', background: 'rgba(255,255,255,0.25)', padding: '1px 5px', borderRadius: '3px' }}>
                {t('BasTab.creditsLabel', { count: isSSH ? '10' : '15' })}
              </span>
            )}
          </button>
        </div>
      </div>

      {triggerError && (
        <div style={{ marginBottom: '16px', padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '13px', color: '#991b1b' }}>
          <i className="fas fa-times-circle" style={{ marginRight: '6px' }} />{triggerError}
        </div>
      )}

      {/* Technique coverage chips */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {(isSSH ? [
          { label: t('BasTab.techCredentialFiles'), icon: 'fa-file-alt', color: '#dc2626' },
          { label: t('BasTab.techSudoEscalation'), icon: 'fa-user-shield', color: '#7c3aed' },
          { label: t('BasTab.techSuidBinaries'), icon: 'fa-exclamation-triangle', color: '#d97706' },
          { label: t('BasTab.techDockerSocketEscape'), icon: 'fa-docker', color: '#0891b2' },
          { label: t('BasTab.techProcessEnvSecrets'), icon: 'fa-eye', color: '#dc2626' },
          { label: t('BasTab.techInternalNetworkReach'), icon: 'fa-network-wired', color: '#059669' },
          { label: t('BasTab.techImdsv1LiveTheft'), icon: 'fa-key', color: '#dc2626' },
          { label: t('BasTab.techWritableCronSystemd'), icon: 'fa-clock', color: '#6b7280' },
        ] : [
          { label: t('BasTab.techIamPrivilegeEscalation'), icon: 'fa-user-shield', color: '#7c3aed' },
          { label: t('BasTab.techSecretsManagerAccess'), icon: 'fa-key', color: '#dc2626' },
          { label: t('BasTab.techSsmSecureParamRead'), icon: 'fa-lock-open', color: '#dc2626' },
          { label: t('BasTab.techLambdaEnvVarLeakage'), icon: 'fa-code', color: '#d97706' },
          { label: t('BasTab.techCloudtrailEvasion'), icon: 'fa-eye-slash', color: '#6b7280' },
          { label: t('BasTab.techEc2Imdsv1Exposure'), icon: 'fa-server', color: '#0891b2' },
        ]).map(t => (
          <span key={t.label} style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            padding: '4px 10px', background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: '20px', fontSize: '12px', color: '#374151',
          }}>
            <i className={`fas ${t.icon}`} style={{ color: t.color, fontSize: '11px' }} />
            {t.label}
          </span>
        ))}
      </div>

      {/* Run history */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
          {t('BasTab.runHistoryHeading')}
          <span style={{ marginLeft: '8px', fontWeight: 400, color: '#9ca3af', fontSize: '13px' }}>{t('BasTab.totalRuns', { count: totalRuns })}</span>
        </h4>
        {runsError && (
          <div style={{ marginBottom: '10px', padding: '8px 12px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', color: '#991b1b' }}>
            <i className="fas fa-exclamation-circle" style={{ marginRight: '6px' }} />{runsError}
          </div>
        )}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['#', t('BasTab.colType'), t('BasTab.colStatus'), t('BasTab.colConnector'), t('BasTab.colTarget'), t('BasTab.colTechniques'), t('BasTab.colCritical'), t('BasTab.colHigh'), t('BasTab.colFindings'), t('BasTab.colDuration'), t('BasTab.colTriggered'), t('BasTab.colStarted')].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!runsLoaded && (
                <tr><td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  <i className="fas fa-spinner fa-spin" />
                </td></tr>
              )}
              {runsLoaded && runs.map(r => {
                const s = RUN_STATUS[r.status] || RUN_STATUS.PENDING;
                const isSelected = selectedRun === r.id;
                return (
                  <tr
                    key={r.id}
                    onClick={() => { setSelectedRun(isSelected ? null : r.id); }}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: isSelected ? '#eff6ff' : undefined }}
                  >
                    <td style={{ padding: '9px 12px', color: '#9ca3af' }}>#{r.id}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                        background: r.simulationType === 'SSH' ? '#f0fdf4' : r.simulationType === 'AZURE' ? '#eff6ff' : '#eff6ff',
                        color: r.simulationType === 'SSH' ? '#166534' : r.simulationType === 'AZURE' ? '#0369a1' : '#1e40af',
                      }}>
                        <i className={`fas ${r.simulationType === 'SSH' ? 'fa-terminal' : r.simulationType === 'AZURE' ? 'fa-microsoft' : 'fa-cloud'}`} style={{ marginRight: '4px' }} />
                        {r.simulationType || 'AWS'}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ backgroundColor: s.backgroundColor, color: s.color, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                        <i className={`fas ${s.icon}`} style={{ marginRight: '4px' }} />{r.status}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px', color: '#374151' }}>{r.connectorName || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#6b7280', fontFamily: 'monospace', fontSize: '11px' }}>
                      {r.simulationType === 'SSH'
                        ? (r.targetHost ? `${r.remoteUser || '?'}@${r.remoteHostname || r.targetHost}` : '—')
                        : (r.accountId || '—')}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#374151', textAlign: 'center' }}>{r.techniquesRun || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      {r.criticalCount > 0
                        ? <span style={{ color: '#991b1b', fontWeight: 700 }}>{r.criticalCount}</span>
                        : <span style={{ color: '#9ca3af' }}>0</span>}
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      {r.highCount > 0
                        ? <span style={{ color: '#9a3412', fontWeight: 600 }}>{r.highCount}</span>
                        : <span style={{ color: '#9ca3af' }}>0</span>}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#374151' }}>{r.findingsCount}</td>
                    <td style={{ padding: '9px 12px', color: '#6b7280' }}>{duration(r.startedAt, r.completedAt)}</td>
                    <td style={{ padding: '9px 12px', color: '#6b7280' }}>{r.triggeredBy}</td>
                    <td style={{ padding: '9px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{ts(r.startedAt)}</td>
                  </tr>
                );
              })}
              {runsLoaded && runs.length === 0 && (
                <tr><td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  {t('BasTab.emptyRuns')}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalRuns > 20 && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'center' }}>
            <button onClick={() => setRunsPage(p => Math.max(0, p - 1))} disabled={runsPage === 0}
              style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: runsPage === 0 ? 'default' : 'pointer', background: '#fff', fontSize: '13px' }}>
              {t('BasTab.btnPrev')}
            </button>
            <span style={{ alignSelf: 'center', fontSize: '13px', color: '#6b7280' }}>{t('BasTab.pageLabel', { page: runsPage + 1 })}</span>
            <button onClick={() => setRunsPage(p => p + 1)} disabled={(runsPage + 1) * 20 >= totalRuns}
              style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px' }}>
              {t('BasTab.btnNext')}
            </button>
          </div>
        )}
      </div>

      {/* BAS Findings */}
      <div>
        <h4 style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
          {selectedRun ? t('BasTab.findingsFromRun', { runId: selectedRun }) : t('BasTab.allFindings')}
          {selectedRun && (
            <button onClick={() => setSelectedRun(null)} style={{ marginLeft: '10px', fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              {t('BasTab.clearFilter')}
            </button>
          )}
          <span style={{ marginLeft: '8px', fontWeight: 400, color: '#9ca3af', fontSize: '13px' }}>{t('BasTab.findingsCount', { count: findings.length })}</span>
        </h4>

        {!findingsLoaded ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            <i className="fas fa-spinner fa-spin" />
          </div>
        ) : findings.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}>
            {selectedRun ? t('BasTab.emptyFindingsForRun') : t('BasTab.emptyFindings')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {findings.map(f => {
              const sev = SEV_STYLE[f.severity] || SEV_STYLE.INFO;
              const mitreId = f.mitreTechniqueId;
              const mitre = mitreId && MITRE[mitreId];
              const isOpen = expandedFinding === f.id;
              return (
                <div key={f.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  {/* Row */}
                  <div
                    onClick={() => setExpandedFinding(isOpen ? null : f.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', cursor: 'pointer', background: '#fff' }}
                  >
                    <span style={{ backgroundColor: sev.backgroundColor, color: sev.color, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {f.severity}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: '#111827' }}>{f.title}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        <span style={{ marginRight: '12px' }}><i className="fas fa-flask" style={{ marginRight: '4px', color: '#dc2626' }} />{t('BasTab.basVerified')}</span>
                        {mitreId && (
                          <span style={{ marginRight: '12px' }}>
                            <i className="fas fa-shield-alt" style={{ marginRight: '4px', color: '#7c3aed' }} />
                            {mitreId}{mitre ? ` · ${mitre.tactic}` : ''}
                          </span>
                        )}
                        <span style={{ color: '#9ca3af', fontSize: '11px' }}>{f.findingType}</span>
                      </div>
                    </div>
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ color: '#9ca3af', fontSize: '12px' }} />
                  </div>

                  {/* Expanded */}
                  {isOpen && (
                    <div style={{ padding: '16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                      <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>{f.description}</p>

                      {/* Evidence */}
                      {f.evidence && Object.keys(f.evidence).length > 0 && (
                        <div style={{ marginBottom: '14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              <i className="fas fa-microscope" style={{ marginRight: '6px', color: '#dc2626' }} />{t('BasTab.evidence')}
                            </span>
                            <button
                              onClick={() => copyText(JSON.stringify(f.evidence, null, 2), `ev-${f.id}`)}
                              style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#374151' }}
                            >
                              <i className={`fas ${copied === `ev-${f.id}` ? 'fa-check' : 'fa-copy'}`} style={{ marginRight: '4px' }} />
                              {copied === `ev-${f.id}` ? t('BasTab.copied') : t('BasTab.copyJson')}
                            </button>
                          </div>
                          <pre style={{
                            background: '#0f172a', color: '#e2e8f0', padding: '12px 14px',
                            borderRadius: '6px', fontSize: '11px', overflow: 'auto',
                            maxHeight: '220px', margin: 0, lineHeight: '1.6',
                          }}>
                            {JSON.stringify(f.evidence, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Remediation */}
                      {f.remediationSteps && (
                        <div style={{ marginBottom: '14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              <i className="fas fa-wrench" style={{ marginRight: '6px', color: '#059669' }} />{t('BasTab.remediation')}
                            </span>
                            <button
                              onClick={() => copyText(f.remediationSteps, `rem-${f.id}`)}
                              style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#374151' }}
                            >
                              <i className={`fas ${copied === `rem-${f.id}` ? 'fa-check' : 'fa-copy'}`} style={{ marginRight: '4px' }} />
                              {copied === `rem-${f.id}` ? t('BasTab.copied') : t('BasTab.copySteps')}
                            </button>
                          </div>
                          <pre style={{
                            background: '#1e293b', color: '#e2e8f0', padding: '12px 14px',
                            borderRadius: '6px', fontSize: '12px', overflow: 'auto',
                            whiteSpace: 'pre-wrap', margin: 0, lineHeight: '1.7',
                          }}>
                            {f.remediationSteps}
                          </pre>
                        </div>
                      )}

                      {/* MITRE card */}
                      {mitre && (
                        <div style={{
                          background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '6px',
                          padding: '10px 14px', fontSize: '12px',
                        }}>
                          <div style={{ fontWeight: 600, color: '#4c1d95', marginBottom: '4px' }}>
                            <i className="fas fa-shield-alt" style={{ marginRight: '6px' }} />MITRE ATT&CK
                          </div>
                          <div style={{ color: '#374151' }}>
                            <strong>{mitreId}</strong> — {mitre.name}
                          </div>
                          <div style={{ color: '#6b7280', marginTop: '2px' }}>{t('BasTab.tacticLabel', { tactic: mitre.tactic })}</div>
                          <a
                            href={`https://attack.mitre.org/techniques/${mitreId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '11px', color: '#7c3aed', textDecoration: 'none', marginTop: '4px', display: 'inline-block' }}
                          >
                            {t('BasTab.viewOnMitre')}
                          </a>
                        </div>
                      )}

                      {/* Compliance */}
                      {f.complianceControls && f.complianceControls.length > 0 && (
                        <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {f.complianceControls.map(c => (
                            <span key={c} style={{ padding: '2px 8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '11px', color: '#1e40af' }}>
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
